import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  NgZone,
  OnDestroy,
  signal,
} from '@angular/core';
import { AssessmentStatus, AssessmentTimerLayout } from './assessment-timer.types';
import { formatElapsedTime } from './format-elapsed-time.util';

/**
 * Assessment Timer Component
 *
 * A global Assessment module widget that displays elapsed time for the current assessment in HH:MM:SS format.
 * Positioned in the top-right of the assessment shell header. This is NOT a drag-drop control for Template Manager.
 *
 * Core Behavior:
 *
 * - Auto-starts when assessment opened first time and status moves to 'in-progress'
 * - Counts up (not down) from zero in HH:MM:SS format
 * - Supports long multi-hour visits without automatic reset
 * - Continues running when NP navigates to other sections/tabs within same assessment
 * - Resumes from previous elapsed time when assessment reopened (based on stored timestamps)
 * - Stops when assessment submitted/completed
 * - Read-only: NP cannot manually start/stop/reset or edit the timer
 * - Displays "Assessment Time" label (standard across all assessments)
 *
 * Technical Features:
 *
 * - Elapsed time calculated from timestamps (not client-side accumulation)
 * - Survives page reloads without resetting
 * - Pauses UI updates when browser tab hidden (Page Visibility API optimization)
 * - Resumes with correct time when tab becomes visible
 * - Graceful error handling: shows '--:--:--' if timestamps unavailable
 * - Styled as small neutral chip following design system tokens
 * - WCAG 2.1 AA compliant
 *
 * @example
 *   ```html
 *   <hx-assessment-timer
 *     [status]="assessmentStatus"
 *     [startTime]="startTime"
 *     [completedTime]="completionTime"
 *   />
 *   ```;
 */
@Component({
  selector: 'hx-assessment-timer',
  templateUrl: './assessment-timer.component.html',
  styleUrls: ['./assessment-timer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class HxAssessmentTimerComponent implements OnDestroy {
  /** Show/hide timer (default: true) */
  readonly visible = input<boolean>(true);

  /** Layout orientation: 'vertical' (default, stacked) or 'horizontal' (label on left, for wider headers) */
  readonly layout = input<AssessmentTimerLayout>('vertical');

  /** Current assessment status */
  readonly status = input.required<AssessmentStatus>();

  /** Date/time when assessment started (ISO string, Date object, or timestamp) */
  readonly startTime = input<Date | string | number | undefined>(undefined);

  /** Date/time when assessment completed (ISO string, Date object, or timestamp) */
  readonly completedTime = input<Date | string | number | undefined>(undefined);

  /** System-level pause state (for programmatic control, not user-initiated) */
  readonly isPaused = input<boolean>(false);

  /** Optional custom time source for testing (default: Date.now) */
  readonly nowSource = input<(() => number) | undefined>(undefined);

  /** Additional CSS class for custom styling */
  readonly className = input<string>('');

  /** Test identifier for E2E tests */
  readonly testId = input<string>('assessment-timer');

  // Internal state
  private readonly ngZone = inject(NgZone);
  private readonly currentTime = signal(Date.now());
  private readonly isAutoPaused = signal(false); // Auto-pause when tab is hidden
  private intervalId: number | null = null;
  private previousTimeChars: string[] = [];
  private animationCounters: number[] = [0, 0, 0, 0, 0, 0, 0, 0];
  private pausedElapsedMs: number | null = null;
  private resumedAtMs: number | null = null;

  /** Convert date/time input to milliseconds */
  private toMilliseconds(value: Date | string | number | undefined): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (value instanceof Date) {
      return value.getTime();
    }
    if (typeof value === 'string') {
      const timestamp = new Date(value).getTime();
      return isNaN(timestamp) ? undefined : timestamp;
    }
    return undefined;
  }

  /** Whether timing data is unavailable */
  protected readonly error = computed(() => {
    const start = this.toMilliseconds(this.startTime());
    return start === undefined || start === null;
  });

  /** Whether timer is actively counting */
  protected readonly isRunning = computed(() => {
    return (
      !this.error() &&
      this.status() === 'in-progress' &&
      this.completedTime() === undefined &&
      !this.isPaused() &&
      !this.isAutoPaused()
    );
  });

  /** Calculate elapsed time in milliseconds */
  protected readonly elapsedMs = computed(() => {
    if (this.error()) {
      return 0;
    }

    // If paused (manual or automatic), return the frozen elapsed time
    if ((this.isPaused() || this.isAutoPaused()) && this.pausedElapsedMs !== null) {
      return this.pausedElapsedMs;
    }

    const now = this.nowSource();
    const start = this.toMilliseconds(this.startTime()) ?? 0;

    // If we've resumed from a pause, calculate elapsed time accounting for the pause
    if (this.resumedAtMs !== null && this.pausedElapsedMs !== null) {
      const currentTime = now ? now() : this.currentTime();
      const timeSinceResume = currentTime - this.resumedAtMs;
      return this.pausedElapsedMs + timeSinceResume;
    }

    const end = this.toMilliseconds(this.completedTime()) ?? (now ? now() : this.currentTime());
    const elapsed = end - start;

    return Math.max(0, elapsed);
  });

  /** Formatted time display (HH:MM:SS format for both vertical and horizontal layouts, or --:--:-- on error) */
  protected readonly displayTime = computed(() => {
    if (this.error()) {
      return '--:--:--';
    }
    const fullTime = formatElapsedTime(this.elapsedMs());
    return fullTime;
  });

  /** Split time into individual characters for animation */
  protected readonly timeCharacters = computed(() => {
    const time = this.displayTime();
    const currentChars = time.split('');

    // Initialize previousTimeChars on first run to prevent all digits from animating
    if (this.previousTimeChars.length === 0) {
      this.previousTimeChars = [...currentChars];
      // Return without animation on first render
      return currentChars.map((char, index) => ({
        char,
        key: `${char}-${index}-${this.animationCounters[index]}`,
        isDigit: char !== ':',
        isSeparator: char === ':',
      }));
    }

    // Detect format: HH:MM:SS (8 chars) or MM:SS (5 chars)
    const isFullFormat = currentChars.length === 8;

    // For HH:MM:SS: minutes at positions 3,4 and seconds at 6,7
    // For MM:SS: minutes at positions 0,1 and seconds at 3,4
    const minutePos1 = isFullFormat ? 3 : 0;
    const minutePos2 = isFullFormat ? 4 : 1;
    const secondPos1 = isFullFormat ? 6 : 3;
    const secondPos2 = isFullFormat ? 7 : 4;

    const minutesChanged =
      this.previousTimeChars[minutePos1] !== currentChars[minutePos1] ||
      this.previousTimeChars[minutePos2] !== currentChars[minutePos2];

    const secondsChanged =
      this.previousTimeChars[secondPos1] !== currentChars[secondPos1] ||
      this.previousTimeChars[secondPos2] !== currentChars[secondPos2];

    // Increment counters for changed positions
    currentChars.forEach((char, index) => {
      const actuallyChanged = this.previousTimeChars[index] !== char;

      // For minutes: increment both if either changed
      if ((index === minutePos1 || index === minutePos2) && minutesChanged) {
        this.animationCounters[index]++;
      }
      // For seconds: increment both if either changed
      else if ((index === secondPos1 || index === secondPos2) && secondsChanged) {
        this.animationCounters[index]++;
      }
      // For hours (HH:MM:SS only, positions 0 and 1): only increment if that specific digit changed
      else if (isFullFormat && (index === 0 || index === 1) && actuallyChanged) {
        this.animationCounters[index]++;
      }
    });

    const result = currentChars.map((char, index) => ({
      char,
      key: `${char}-${index}-${this.animationCounters[index]}`,
      isDigit: char !== ':',
      isSeparator: char === ':',
    }));

    this.previousTimeChars = [...currentChars];
    return result;
  });

  constructor() {
    // Set up automatic pause/resume based on page visibility
    if (typeof document !== 'undefined') {
      const handleVisibilityChange = (): void => {
        this.isAutoPaused.set(document.hidden);
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup on destroy
      effect((onCleanup) => {
        onCleanup(() => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        });
      });
    }

    // Set up interval for live updates
    effect(() => {
      if (this.isRunning()) {
        // When resuming from pause, capture the resume time and sync currentTime
        if (this.pausedElapsedMs !== null) {
          const now = this.nowSource();
          const currentNow = now ? now() : Date.now();
          this.resumedAtMs = currentNow;
          // Force currentTime signal to sync immediately to prevent flicker
          this.currentTime.set(currentNow);
        } else {
          // First time running - clear resume tracking
          this.resumedAtMs = null;
        }
        this.startInterval();
      } else {
        // Capture elapsed time when pausing (only if actually paused, not stopped/error)
        const isPausedState = this.isPaused() || this.isAutoPaused();
        if (isPausedState && !this.error() && this.completedTime() === undefined) {
          const now = this.nowSource();

          // Calculate current elapsed time
          if (this.resumedAtMs !== null && this.pausedElapsedMs !== null) {
            // We were running after a resume - add time since resume to previous paused elapsed
            const currentTime = now ? now() : Date.now();
            const timeSinceResume = currentTime - this.resumedAtMs;
            this.pausedElapsedMs = this.pausedElapsedMs + timeSinceResume;
          } else {
            // First pause - calculate from start
            const start = this.toMilliseconds(this.startTime()) ?? 0;
            const end = now ? now() : Date.now();
            this.pausedElapsedMs = Math.max(0, end - start);
          }
          this.resumedAtMs = null;
        } else if (this.completedTime() !== undefined || (!this.isPaused() && !this.isAutoPaused())) {
          // If completed or not paused (stopped for other reason), clear pause tracking
          this.pausedElapsedMs = null;
          this.resumedAtMs = null;
        }
        this.stopInterval();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopInterval();
  }

  private startInterval(): void {
    if (this.intervalId === null) {
      this.ngZone.runOutsideAngular(() => {
        this.intervalId = window.setInterval(() => {
          this.currentTime.set(Date.now());
        }, 1000);
      });
    }
  }

  private stopInterval(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
